import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import type { User } from "../../lib/types";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../ui/table";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import ManageAccessPopover from "./ManageAccessPopover";

const PAGE_SIZE = 20;

export const UsersTable = () => {
	const [users, setUsers] = useState<User[]>([]);
	const { user } = useAuth();
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
	const [userRoles, setUserRoles] = useState<
		Record<string, { role_name: string; role_id: number } | null>
	>({});

	// Filters & pagination
	const [emailSearch, setEmailSearch] = useState("");
	const [roleFilter, setRoleFilter] = useState<string>("all");
	const [page, setPage] = useState(1);

	useEffect(() => {
		fetchUsers();
	}, []);

	const fetchUsers = async () => {
		const result = await api.getUserList(user?.id || "");
		const fetchedUsers: User[] = result.results;
		setUsers(fetchedUsers);

		const roleResults = await Promise.all(
			fetchedUsers.map((u) => api.getUserRole(u.id)),
		);
		const rolesMap: Record<
			string,
			{ role_name: string; role_id: number } | null
		> = {};
		fetchedUsers.forEach((u, i) => {
			rolesMap[u.id] =
				roleResults[i]?.success && roleResults[i].data
					? {
							role_name: roleResults[i].data.role_name,
							role_id: roleResults[i].data.role_id,
						}
					: null;
		});
		setUserRoles(rolesMap);
	};

	const fetchUserRoles = async (fetchedUsers: User[]) => {
		const roleResults = await Promise.all(
			fetchedUsers.map((u) => api.getUserRole(u.id)),
		);
		const rolesMap: Record<
			string,
			{ role_name: string; role_id: number } | null
		> = {};
		fetchedUsers.forEach((u, i) => {
			rolesMap[u.id] =
				roleResults[i]?.success && roleResults[i].data
					? {
							role_name: roleResults[i].data.role_name,
							role_id: roleResults[i].data.role_id,
						}
					: null;
		});
		setUserRoles(rolesMap);
	};

	const handleManageAccessClick = (userId: string) => {
		setSelectedUserId(userId);
		setIsPopoverOpen(true);
	};

	const closePopover = () => {
		setIsPopoverOpen(false);
		setSelectedUserId(null);
		fetchUserRoles(users);
	};

	// Derive unique role names for the filter dropdown
	const uniqueRoles = useMemo(() => {
		const names = new Set<string>();
		for (const roleData of Object.values(userRoles)) {
			if (roleData?.role_name) names.add(roleData.role_name);
		}
		return Array.from(names).sort();
	}, [userRoles]);

	// Apply email search + role filter
	const filteredUsers = useMemo(() => {
		return users.filter((u) => {
			const matchesEmail = u.email
				.toLowerCase()
				.includes(emailSearch.toLowerCase().trim());
			const matchesRole =
				roleFilter === "all"
					? true
					: roleFilter === "no_role"
						? !userRoles[u.id]
						: userRoles[u.id]?.role_name === roleFilter;
			return matchesEmail && matchesRole;
		});
	}, [users, userRoles, emailSearch, roleFilter]);

	// Reset to page 1 whenever filters change
	useEffect(() => {
		setPage(1);
	}, [emailSearch, roleFilter]);

	const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
	const pagedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	return (
		<div className="space-y-4 bg-white dark:bg-gray-900">
			{/* Filters */}
			<div className="flex flex-wrap gap-3 items-center">
				<input
					type="text"
					placeholder="Search by email..."
					value={emailSearch}
					onChange={(e) => setEmailSearch(e.target.value)}
					className="border rounded-md px-3 py-1.5 text-sm w-64 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
				/>
				<Select value={roleFilter} onValueChange={setRoleFilter}>
					<SelectTrigger className="w-44 h-9 text-sm">
						<SelectValue placeholder="Filter by role" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All Roles</SelectItem>
						<SelectItem value="no_role">No Role</SelectItem>
						{uniqueRoles.map((r) => (
							<SelectItem key={r} value={r}>
								{r}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{(emailSearch || roleFilter !== "all") && (
					<Button
						variant="outline"
						size="sm"
						onClick={() => {
							setEmailSearch("");
							setRoleFilter("all");
						}}
					>
						Clear
					</Button>
				)}
				<span className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
					{filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""}
				</span>
			</div>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Email</TableHead>
						<TableHead>Role</TableHead>
						<TableHead>Actions</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{pagedUsers.map((u) => (
						<TableRow key={u.id}>
							<TableCell>{u.email}</TableCell>
							<TableCell>
								{userRoles[u.id] ? (
									<Badge variant="secondary">
										{userRoles[u.id]!.role_name}
									</Badge>
								) : (
									<span className="text-sm text-gray-400 dark:text-gray-500">
										No Role
									</span>
								)}
							</TableCell>
							<TableCell>
								<Button
									variant="outline"
									onClick={() => handleManageAccessClick(u.id)}
								>
									Manage Access
								</Button>
							</TableCell>
						</TableRow>
					))}
					{pagedUsers.length === 0 && (
						<TableRow>
							<TableCell colSpan={3} className="text-center py-8 text-gray-400">
								No users match the current filters.
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex items-center justify-between pt-2">
					<span className="text-sm text-gray-500 dark:text-gray-400">
						Page {page} of {totalPages}
					</span>
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							disabled={page === 1}
							onClick={() => setPage((p) => p - 1)}
						>
							Previous
						</Button>
						<Button
							variant="outline"
							size="sm"
							disabled={page === totalPages}
							onClick={() => setPage((p) => p + 1)}
						>
							Next
						</Button>
					</div>
				</div>
			)}

			{isPopoverOpen && selectedUserId && (
				<ManageAccessPopover userId={selectedUserId} onClose={closePopover} />
			)}
		</div>
	);
};
